import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAnnouncements } from '../context/AnnouncementContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faExclamationTriangle,
  faExclamationCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

const DISMISSED_KEY = 'cabinet_dismissed_announcements';
const CYCLE_MS = 6000;

const AnnouncementBanner: React.FC = () => {
  const { activeAnnouncements, setBannerHeight } = useAnnouncements();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [slideOut, setSlideOut] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(DISMISSED_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const bannerRef = useRef<HTMLDivElement>(null);

  const visibleAnnouncements = activeAnnouncements.filter(a => !dismissed.has(a.id));

  // Measure height
  useEffect(() => {
    if (visibleAnnouncements.length > 0 && !slideOut) {
      const measure = () => {
        if (bannerRef.current) setBannerHeight(bannerRef.current.offsetHeight);
      };
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    } else {
      setBannerHeight(0);
    }
  }, [visibleAnnouncements.length, slideOut, setBannerHeight]);

  // Auto-cycle
  useEffect(() => {
    if (visibleAnnouncements.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % visibleAnnouncements.length);
    }, CYCLE_MS);
    return () => clearInterval(interval);
  }, [visibleAnnouncements.length]);

  // Crossfade when currentIndex changes
  useEffect(() => {
    if (currentIndex !== displayIndex) {
      setFading(true);
      const timeout = setTimeout(() => {
        setDisplayIndex(currentIndex);
        setFading(false);
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, displayIndex]);

  // Keep index in bounds
  useEffect(() => {
    if (currentIndex >= visibleAnnouncements.length && visibleAnnouncements.length > 0) {
      setCurrentIndex(0);
      setDisplayIndex(0);
    }
  }, [currentIndex, visibleAnnouncements.length]);

  const dismiss = useCallback((id: string) => {
    setSlideOut(true);
    setTimeout(() => {
      setDismissed(prev => {
        const next = new Set(prev);
        next.add(id);
        sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(next)));
        return next;
      });
      setSlideOut(false);
    }, 400);
  }, []);

  if (visibleAnnouncements.length === 0) return null;

  const safeDisplay = displayIndex % visibleAnnouncements.length;
  const current = visibleAnnouncements[safeDisplay];
  if (!current) return null;

  const typeStyles: Record<string, { bg: string; icon: typeof faBullhorn; glow: string }> = {
    info: {
      bg: 'bg-gradient-to-r from-accent via-primary to-accent',
      icon: faBullhorn,
      glow: '',
    },
    warning: {
      bg: 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600',
      icon: faExclamationTriangle,
      glow: '',
    },
    urgent: {
      bg: 'bg-gradient-to-r from-red-700 via-red-600 to-red-700',
      icon: faExclamationCircle,
      glow: 'shadow-[0_2px_20px_rgba(220,38,38,0.4)]',
    },
  };

  const style = typeStyles[current.type] || typeStyles.info;

  const sparklePositions = [
    { left: '8%', top: '30%', size: 'w-1 h-1', delay: '0s', opacity: 'opacity-20' },
    { left: '18%', top: '65%', size: 'w-0.5 h-0.5', delay: '1s', opacity: 'opacity-15' },
    { left: '75%', top: '25%', size: 'w-0.5 h-0.5', delay: '0.5s', opacity: 'opacity-15' },
    { left: '85%', top: '60%', size: 'w-1 h-1', delay: '1.5s', opacity: 'opacity-20' },
    { left: '45%', top: '20%', size: 'w-0.5 h-0.5', delay: '2s', opacity: 'opacity-10' },
  ];

  return (
    <div
      ref={bannerRef}
      className={`fixed top-0 left-0 right-0 z-[60] transition-transform duration-400 ease-out ${
        slideOut ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className={`${style.bg} ${style.glow} text-white relative overflow-hidden`}>
        {/* Shimmer light sweep */}
        <div className="announcement-shimmer" />

        {/* Decorative bokeh sparkles */}
        {sparklePositions.map((sp, i) => (
          <div
            key={i}
            className={`absolute ${sp.size} bg-white rounded-full ${sp.opacity} announcement-sparkle hidden sm:block`}
            style={{ left: sp.left, top: sp.top, animationDelay: sp.delay }}
          />
        ))}

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 min-h-[44px]">

            {/* Left: dot indicators */}
            <div className="flex items-center space-x-1.5 flex-shrink-0 min-w-[60px]">
              {visibleAnnouncements.length > 1 &&
                visibleAnnouncements.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`rounded-full transition-all duration-500 ease-out ${
                      i === (currentIndex % visibleAnnouncements.length)
                        ? 'bg-white w-5 h-1.5'
                        : 'bg-white/30 hover:bg-white/50 w-1.5 h-1.5'
                    }`}
                    aria-label={`Annonce ${i + 1}`}
                  />
                ))}
            </div>

            {/* Center: message with crossfade */}
            <div className="flex-1 text-center px-3 sm:px-6 overflow-hidden">
              <div
                className={`flex items-center justify-center transition-all duration-250 ${
                  fading
                    ? 'opacity-0 translate-y-1'
                    : 'opacity-100 translate-y-0'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15 mr-2.5 flex-shrink-0 ${
                  current.type === 'urgent' ? 'animate-pulse' : ''
                }`}>
                  <FontAwesomeIcon icon={style.icon} className="w-3 h-3" />
                </span>
                <span className="text-[13px] sm:text-sm font-medium tracking-wide leading-snug">
                  {current.message}
                </span>
              </div>
            </div>

            {/* Right: close */}
            <div className="flex-shrink-0 min-w-[60px] flex justify-end">
              <button
                onClick={() => dismiss(current.id)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/0 hover:bg-white/15 transition-all duration-200 group"
                aria-label="Fermer l'annonce"
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="w-3 h-3 opacity-70 group-hover:opacity-100 transition-opacity"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar for auto-cycle */}
        {visibleAnnouncements.length > 1 && (
          <div className="h-[2px] bg-white/10">
            <div
              key={`progress-${currentIndex}-${visibleAnnouncements.length}`}
              className="h-full bg-white/30 announcement-progress"
              style={{ animationDuration: `${CYCLE_MS}ms` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBanner;
